import React, { useCallback, useMemo } from 'react';

// Components
import {
  SMPText,
  SMPFlex,
  MatchParticipant,
  SMPDivider,
  SMPImage,
  OddsDisplay,
  SMPContainer,
  SMPLinkWrapper,
} from '@velinsi/smp-widgets-ui';
import TennisMatchContainer from '../../MatchContainer/MatchContainer';
import Score from '../Score/Score';

// Types
import {
  INormalizedTennisMatch,
  INormalizedTennisParticipant,
  INormalizedTennisParticipantDetails,
  ISubscriberInfoFn,
  MatchContainerTypes,
} from '../../../../../../packages/types';
import { LIVE, FINISHED } from '../../../constants/statusTypes';
import { CurrentServer, ParticipantTypes, Winner } from '../../../../../../packages/constants/tennis';
import { LIVESCORE_FULL_TIME_VARIANT, PRIMARY_GRAY_VARIANT } from '../../../../../../packages/constants/statusTextVariants';
import { assetsPlaceholder } from '../../../../../../packages/constants/assetsPlaceholder';
import { GenericLayout, GenericSize, TABULAR_WIDGET_BREAKPOINT_2 } from '../../../../../../packages/constants/generic';
import { styled } from '../../../../../../packages/theme/stitches.config';
import { breakText } from '@sportal365/smp-widget-utils';

export interface IEventInfoProps {
  showOdds?: boolean;
  match: INormalizedTennisMatch;
  participants: INormalizedTennisParticipant[];
  handleClick: ISubscriberInfoFn;
  handleParticipantClick?: (participant: INormalizedTennisParticipantDetails) => void;
  handleEventClick?: (id: string | number) => void;
  isMobile?: boolean;
  showCurrentServer?: boolean;
  matchContainerType?: MatchContainerTypes;
  size?: GenericSize;
  width: number;
  wrapperLink: boolean;
  areBettingOddsOverlapVisible?: boolean;
}

function EventInfo({
  match,
  showOdds,
  participants,
  handleClick,
  handleParticipantClick,
  handleEventClick,
  showCurrentServer,
  matchContainerType,
  size,
  isMobile,
  width,
  wrapperLink = true,
  areBettingOddsOverlapVisible,
}: IEventInfoProps): JSX.Element {
  const abbreviateName = useMemo(() => width < TABULAR_WIDGET_BREAKPOINT_2, [width]);
  const urlTarget = wrapperLink ? undefined : match.urlTarget;
  const resolvedHandleParticipantClick = wrapperLink ? undefined : handleParticipantClick;

  const getMobileTeamDoubleContent = useCallback(
    (participant: INormalizedTennisParticipant, pairIndex: number) => (
      <SMPFlex grow='1' variant='tennisParticipantsCol'>
        <SMPFlex align='center' css={{ pr: '$2' }}>
          <SMPFlex variant='tennisParticipantsCol'>
            {participant.details?.map((participantDetails: INormalizedTennisParticipantDetails) => (
              <MatchParticipant
                isMobile={isMobile}
                imageVariant='mediumLivescoreEntityLogo'
                key={participantDetails.id}
                handleParticipantClick={resolvedHandleParticipantClick}
                participant={participantDetails}
                participantLink={wrapperLink ? undefined : participantDetails?.url}
                participantName={participantDetails?.name}
                urlTarget={urlTarget}
                countryLogo={width < TABULAR_WIDGET_BREAKPOINT_2 ? '' : participantDetails?.country?.assets?.flag?.url || ''}
                winner={participant?.winner}
                abbreviateName={abbreviateName}
              />
            ))}
          </SMPFlex>
          {showCurrentServer && participant.currentServer === CurrentServer.YES ? (
            <SMPImage variant={'tennisParticipantServer'} src={assetsPlaceholder.tennisServer} alt={participant.name} />
          ) : null}
        </SMPFlex>
        {pairIndex + 1 < participants.length && participant.type === ParticipantTypes.TEAM_DOUBLE ? <SMPDivider variant='base' /> : null}
      </SMPFlex>
    ),
    [showCurrentServer, participants.length, isMobile, resolvedHandleParticipantClick, wrapperLink, urlTarget, width, abbreviateName],
  );

  const [_shouldShowDate, matchDate] = match?.date as [boolean, string];

  const matchStatusTextVariant = match?.status === FINISHED.toLocaleLowerCase() ? LIVESCORE_FULL_TIME_VARIANT : PRIMARY_GRAY_VARIANT;
  const statusTextVariant = match?.matchStatus.type === LIVE ? LIVE : matchStatusTextVariant;

  const renderOdds = showOdds && match.odds && match.odds.length > 0;

  const mainContent = useMemo(
    () => (
      <>
        <SMPFlex grow='2' css={{ '& > a:first-child': { mr: '$2' } }}>
          <a href={wrapperLink ? undefined : match.matchLink} target={urlTarget} style={{ display: 'inline-flex', textDecoration: 'none' }}>
            <SMPFlex align='center' justify='center' variant='column' css={{ width: '$7' }}>
              <SMPText size='2' textAlign='center' variant='primaryGray' css={{ mb: '$1' }}>
                {isMobile ? breakText(matchDate) : matchDate}
              </SMPText>

              <SMPText variant={statusTextVariant} size='2' weight={'700'}>
                {match?.status}
              </SMPText>
            </SMPFlex>
          </a>
          <SMPFlex
            justify='center'
            variant='column'
            grow='1'
            css={{
              '@bp0': { width: '110px' },
            }}
          >
            {participants?.map((participant: INormalizedTennisParticipant, index: number) => (
              <React.Fragment key={participant.id}>
                {index === 0 && <ParticipantHeaderPlaceholder />}
                <SMPFlex direction='row' justify='spaceBetween'>
                  {participant?.type === ParticipantTypes.TEAM_DOUBLE ? (
                    isMobile ? (
                      getMobileTeamDoubleContent(participant, index)
                    ) : (
                      <SMPFlex grow='1' direction='row' justify='start' align='center' style={{ justifyContent: 'flex-start' }}>
                        <MatchParticipant
                          imageVariant='mediumLivescoreEntityLogo'
                          handleParticipantClick={resolvedHandleParticipantClick}
                          participant={participant?.details[0]}
                          participantName={participant?.details[0]?.name}
                          participantLink={wrapperLink ? undefined : participant?.details[0]?.url}
                          urlTarget={urlTarget}
                          winner={participant?.winner}
                          isMobile={isMobile}
                          sportEntityVariant={ParticipantTypes.TEAM_DOUBLE}
                          countryLogo={participant?.details[0]?.country?.assets?.flag?.url || ''}
                          currentServer={participant.currentServer}
                          abbreviateName={abbreviateName}
                        />

                        <SMPText
                          variant='tennisSingleEventSeparator'
                          size={{ '@initial': '2', '@bp1': '3' }}
                          weight={participant?.winner === Winner.YES ? '700' : '400'}
                          css={{
                            py: '$1',
                            px: '$1',
                            display: 'flex',
                          }}
                        >
                          |
                        </SMPText>
                        <MatchParticipant
                          imageVariant='mediumLivescoreEntityLogo'
                          handleParticipantClick={resolvedHandleParticipantClick}
                          participant={participant?.details[1]}
                          participantName={participant?.details[1]?.name}
                          participantLink={wrapperLink ? undefined : participant?.details[1]?.url}
                          urlTarget={urlTarget}
                          winner={participant?.winner}
                          isMobile={isMobile}
                          sportEntityVariant={ParticipantTypes.TEAM_DOUBLE}
                          countryLogo={participant?.details[1]?.country?.assets?.flag?.url || ''}
                          currentServer={participant.currentServer}
                          abbreviateName={abbreviateName}
                        />

                        {showCurrentServer && participant.currentServer === CurrentServer.YES ? (
                          <SMPImage variant={'tennisParticipantServer'} src={assetsPlaceholder.tennisServer} alt={participant.name} />
                        ) : null}
                      </SMPFlex>
                    )
                  ) : (
                    <MatchParticipant
                      isMobile={isMobile}
                      imageVariant='mediumLivescoreEntityLogo'
                      handleParticipantClick={resolvedHandleParticipantClick}
                      participant={participant?.details[0]}
                      participantName={participant?.name}
                      participantLink={wrapperLink ? undefined : participant?.details[0]?.url}
                      urlTarget={urlTarget}
                      countryLogo={width < TABULAR_WIDGET_BREAKPOINT_2 ? '' : participant?.details[0]?.country?.assets?.flag?.url || ''}
                      winner={participant?.winner}
                      showCurrentServer={showCurrentServer}
                      currentServer={participant.currentServer}
                      abbreviateName={abbreviateName}
                    />
                  )}
                </SMPFlex>
              </React.Fragment>
            ))}
          </SMPFlex>
        </SMPFlex>

        <Score handleEventClick={wrapperLink ? undefined : handleEventClick} isMobile={isMobile} match={match} width={width} />
      </>
    ),
    [
      abbreviateName,
      getMobileTeamDoubleContent,
      handleEventClick,
      isMobile,
      match,
      matchDate,
      participants,
      resolvedHandleParticipantClick,
      showCurrentServer,
      statusTextVariant,
      urlTarget,
      width,
      wrapperLink,
    ],
  );

  return (
    <div style={{ marginBottom: '1px' }}>
      <TennisMatchContainer
        variant={match?.matchStatus?.type === 'live' ? 'live' : 'base'}
        css={{ backgroundColor: '$rowBackgroundColor', mb: '0px' }}
      >
        <SMPFlex
          css={{
            paddingLeft: wrapperLink ? undefined : '$2',
            paddingRight: wrapperLink ? (size !== GenericSize.SMALL ? '0' : undefined) : '$3',
            py: wrapperLink ? undefined : '$3',
          }}
          variant='livescoreMatch'
          justify='spaceBetween'
          tennisEventInfo={size !== GenericSize.SMALL ? 'default' : 'small'}
        >
          {wrapperLink ? (
            <SMPLinkWrapper
              href={match.matchLink}
              target={match.urlTarget}
              onClick={() => handleEventClick && match.id && handleEventClick(match.id)}
            >
              {mainContent}
            </SMPLinkWrapper>
          ) : (
            mainContent
          )}
          {size !== GenericSize.SMALL &&
            (renderOdds ? (
              <OddsDisplay
                odds={match.odds!}
                handleClick={handleClick}
                marketSelectionDisplay={false}
                alignment={GenericLayout.VERTICAL}
                isStandalone={matchContainerType === 'singleEvent'}
                isHeader
                areBettingOddsOverlapVisible={areBettingOddsOverlapVisible}
              />
            ) : (
              <OddsPlaceholder />
            ))}
        </SMPFlex>
      </TennisMatchContainer>

      {size !== GenericSize.DEFAULT && renderOdds && (
        <SMPContainer variant='paddinglessContainer'>
          <OddsDisplay
            odds={match.odds!}
            handleClick={handleClick}
            marketSelectionDisplay={false}
            isStandalone={matchContainerType === 'singleEvent'}
            isHeader
            areBettingOddsOverlapVisible={areBettingOddsOverlapVisible}
          />
        </SMPContainer>
      )}
    </div>
  );
}

const ParticipantHeaderPlaceholder = styled.withConfig({ componentId: 'EventInfo', displayName: 'ParticipantHeaderPlaceholder' })('div', {
  height: '1em',
  fontSize: '$2_5',
});

const OddsPlaceholder = styled.withConfig({ componentId: 'EventInfo', displayName: 'OddsPlaceholder' })('div', {
  width: '100px',
  px: '$3',
  backgroundColor: '$oddsContainerPrimaryBgColor',
});

export default EventInfo;
